import { Callout, Card, Container, PageContainer } from '@amelio/ui-library';

import { AssignReviewerHeader } from './AssignReviewerHeader/AssignReviewerHeader';
import {
    HEADER_LAYOUT_CLASS_NAME,
    REVIEWEES_LAYOUT_CLASS_NAME,
} from './AssignReviewerHeader/assignReviewerHeaderLayout';
import { ReviewerSelection } from './ReviewerSelection/ReviewerSelection';

import type { User } from '@/types/directory/User';
import type { Reviewee } from '@/utils/reviewEvaluatorSelection';

import { FeedbackCommentRepliesCard } from '@/components/FeedbackCommentRepliesCard/FeedbackCommentRepliesCard';
import { FeedbackPrivacyCard } from '@/components/FeedbackPrivacyCard/FeedbackPrivacyCard';
import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import {
    NEW_FEEDBACK_360_COMMENT_REPLIES_ENABLED,
    type Feedback360SettingsLockValue,
} from '@/constants/review/Feedback360Settings';
import { FeedbackPrivacy, type FeedbackPrivacyValue } from '@/constants/review/FeedbackPrivacy';

interface AddReviewerParams {
    revieweeId: string;
    reviewers: User[];
}

interface RemoveReviewerParams {
    revieweeId: string;
    reviewerId: string;
}

interface AssignReviewerProps {
    reviewees: Reviewee[];
    isLoading?: boolean;
    isSaving?: boolean;
    isEvaluationLaunched?: boolean;
    isReviewerRemovalLocked?: boolean;
    isReviewerAdditionLocked?: boolean;
    feedbackPrivacy?: FeedbackPrivacyValue;
    feedback360SettingsLock?: Feedback360SettingsLockValue;
    onFeedbackPrivacyChange?: (feedbackPrivacy: FeedbackPrivacyValue) => void;
    /** Why the reviewers cannot go on as they are, said last so it sits above the Next button. */
    reviewersErrorMessage?: string;
    commentRepliesEnabled?: boolean;
    onCommentRepliesEnabledChange?: (commentRepliesEnabled: boolean) => void;
    onAddReviewer: (params: AddReviewerParams) => void;
    onRemoveReviewer: (params: RemoveReviewerParams) => void;
    onRemoveReviewee?: (revieweeId: string) => void;
}

export const AssignReviewer = ({
    reviewees,
    isLoading,
    isSaving,
    isEvaluationLaunched,
    isReviewerRemovalLocked,
    isReviewerAdditionLocked,
    feedbackPrivacy = FeedbackPrivacy.NAMED,
    feedback360SettingsLock,
    onFeedbackPrivacyChange,
    reviewersErrorMessage,
    commentRepliesEnabled = NEW_FEEDBACK_360_COMMENT_REPLIES_ENABLED,
    onCommentRepliesEnabledChange,
    onAddReviewer,
    onRemoveReviewer,
    onRemoveReviewee,
}: AssignReviewerProps) => {
    return (
        <PageContainer className={'flex flex-1 flex-col'}>
            {isLoading ? (
                <Skeleton>
                    <Container className={HEADER_LAYOUT_CLASS_NAME}>
                        {/* Header Card Section */}
                        <AssignReviewerHeaderSkeleton />

                        {/* Reviewees Section */}
                        <div className={REVIEWEES_LAYOUT_CLASS_NAME}>
                            <Container
                                mt={'section'}
                                className={'flex-col justify-end'}
                            >
                                <RevieweesListSkeleton />
                            </Container>
                        </div>
                    </Container>
                </Skeleton>
            ) : (
                <AssignReviewerHeader
                    reviewees={reviewees}
                    onRemoveReviewee={onRemoveReviewee}
                    isEvaluationLaunched={isEvaluationLaunched}
                />
            )}

            <ReviewerSelection
                reviewees={reviewees}
                isLoading={isLoading}
                isSaving={isSaving}
                isReviewerRemovalLocked={isReviewerRemovalLocked}
                isReviewerAdditionLocked={isReviewerAdditionLocked}
                onAddReviewer={onAddReviewer}
                onRemoveReviewer={onRemoveReviewer}
            />

            {!isLoading && (
                <>
                    <FeedbackPrivacyCard
                        className={'mt-8'}
                        value={feedbackPrivacy}
                        lock={feedback360SettingsLock}
                        onChange={onFeedbackPrivacyChange}
                    />

                    <FeedbackCommentRepliesCard
                        className={'mt-6'}
                        value={commentRepliesEnabled}
                        lock={feedback360SettingsLock}
                        onChange={onCommentRepliesEnabledChange}
                    />
                </>
            )}

            {reviewersErrorMessage && (
                <Callout
                    tone={'warning'}
                    className={'mt-6'}
                >
                    {reviewersErrorMessage}
                </Callout>
            )}
        </PageContainer>
    );
};

// Skeleton Components for better readability
const AssignReviewerHeaderSkeleton = () => (
    <Card
        className={'max-w-150 self-start px-8 py-8'}
        borderRadius={'2xl'}
    >
        <div className={'flex items-center gap-6'}>
            <div
                className={
                    'bg-primary-50 flex aspect-square h-16 w-16 shrink-0 items-center justify-center rounded-full'
                }
            >
                <Bone className={'h-8 w-8'} />
            </div>

            <div>
                <Bone className={'h-8 w-48'} />

                <Bone className={'mt-1 h-4 w-64'} />
            </div>
        </div>
    </Card>
);

const RevieweesListSkeleton = () => (
    <Card
        className={'relative'}
        borderRadius={'2xl'}
    >
        <Bone className={'absolute -top-3.5 -right-6 h-6 w-20 rounded-full'} />

        <Container
            direction={'col'}
            gap={'sm'}
            px={'sm'}
            pb={'sm'}
            mt={'xs'}
        >
            {Array.from({ length: 3 }).map((_, index) => (
                <RevieweeCardSkeleton key={index} />
            ))}
        </Container>
    </Card>
);

const RevieweeCardSkeleton = () => (
    <Card
        className={'flex flex-row items-center justify-between gap-2 bg-white px-2'}
        padding={'none'}
        borderRadius={'2xl'}
    >
        <div className={'flex items-center gap-2'}>
            <Bone className={'h-12 w-12 rounded-full'} />

            <div>
                <Bone className={'h-5 w-32'} />

                <Bone className={'mt-1 h-3 w-24'} />
            </div>
        </div>

        <div className={'bg-base-100 rounded-2xl p-4'}>
            <Bone className={'h-6 w-6'} />
        </div>
    </Card>
);
